import { all, put, select, takeLatest } from 'redux-saga/effects';
import { FinancialActionTypes } from './financials-actions';
import { AlertActionType } from '@store/alert/alert-action';
import discountValidation from '@api/financials/discount-validation';
import orderItem from '@api/financials/order-item';
import sendOrder from '@api/financials/send-order';
import { financeStore } from './financials-store';
import ReactGA from 'react-ga4';
import uploadCardToCardReceiptFileApi from '@api/financials/upload-card-to-card-receipt-file';
import storeCardToCardReceiptApi from '@api/financials/store-card-to-card-receipt';
import cancelOrder from '@api/financials/cancel-order';
import { homeStore } from '@store/home-screen/home-store';
import routes from '@routes';
import getValidDiscountCodes from '@api/financials/valid-discount-codes';

function* getOrderItemWatcher() {
  try {
    const { orderItemId } = yield select(financeStore);

    const orderItemResponse: { data: { data: { order_reference_id_affix: string, order_client_full_name: string, order_service_title: string, created_at_affix: string, order_client_address: string, price: number, payment_type: string, credit_price: number, payment_tracking_code: string, payment_status: string }, pay_routes: { way_route_1: { gateways: Array<object>, default_gateway: string }, card_to_card: any } } } = yield orderItem({
      orderItemId: orderItemId,
      cache: 'no-cache',
    });
    yield put({
      type: FinancialActionTypes.SET_ORDER_ITEM,
      data: {
        orderItemDetails: orderItemResponse?.data?.data,
        orderPaymentDetails: orderItemResponse?.data?.pay_routes?.way_route_1,
        cardToCardData: orderItemResponse?.data?.pay_routes?.card_to_card,
      },
    });
  } catch (error: any) {
    const { navigation } = yield select(homeStore);
    yield put({
      type: AlertActionType.SHOW_ALERT,
      text: error?.message || 'قیمت گذاری قبلی سفارش شما منقضی شده است. منتظر دریافت لینک جدید تا ۳۰ دقیقه دیگر باشید.',
      severity: 'danger',
    });

    if (error?.code === 406) {
      yield navigation.replace(routes['route.order.expired']);
    } else if (error?.code !== 401) {
      yield navigation.replace(routes['route.client.orders']);
    }
  }
}

function* getCodeValidationWatcher() {
  try {
    const { discountCode, orderItemId } = yield select(financeStore);

    const discountValidationResponse: { data: { coupon_price: number } } = yield discountValidation({
      orderItemId: orderItemId,
      discountCode: discountCode,
      cache: 'no-cache',
    });

    yield put({
      type: FinancialActionTypes.SET_DISCOUNT_VALIDATION,
      data: {
        discountAmount: discountValidationResponse?.data?.coupon_price,
        discountCode: discountCode,
        discountValid: true,
      },
    });
    yield put({
      type: AlertActionType.SHOW_ALERT,
      text: 'کد تخفیف شما با موفقیت اعمال شد',
      severity: 'success',
    });
  } catch (error: any) {
    yield put({
      type: AlertActionType.SHOW_ALERT,
      text: error?.message || 'اعمال کد تخفیف با خطا مواجه شده است، لطفا با پشتیبانی تماس بگیرید.',
      severity: 'danger',
    });

    yield put({
      type: FinancialActionTypes.SET_DISCOUNT_VALIDATION,
      data: {
        discountAmount: 0,
        discountValid: false,
        discountCode: null,
      },
    });
  }
}

function* sendOrderItemWatcher() {
  try {
    const { navigation } = yield select(homeStore);
    const { orderItemId, selectedGateway, discountValid, walletSelected, discountCode, moduleName, isShop } = yield select(financeStore);
    const orderItemResponse: { data: {}, extra: { virtual_pay: string, redirect: string } } = yield sendOrder({
      orderItemId: orderItemId,
      gateway: selectedGateway,
      discountAllowed: discountValid,
      creditAllowed: walletSelected,
      discountCode: discountCode,
      cache: 'no-cache',
    });
    if (moduleName === 'pharmacy' && !isShop) {
      // ReactGA.ga('send', 'event', 'Rx', 'Payment', 'go to rx payment');
      // @ts-ignore
      if (window && window.gtag) {
        // @ts-ignore
        window.gtag('event', 'conversion', {
          'send_to': 'AW-631414638/hRylCKuAs4MaEO6-iq0C',
          'value': 3.0,
          'currency': 'USD',
          'transaction_id': '',
        });
      }
      // @ts-ignore
      if (window && window._paq) {
        // @ts-ignore
        window._paq.push(['trackGoal', 2, 3000]);
      }
    }
    if (moduleName === 'laboratory') {
      ReactGA.ga('send', 'event', 'laboratory', 'payment', 'Open the payment gateway page');
    }
    if (moduleName === 'consult') {
      ReactGA.ga('send', 'event', 'consult', 'payment', 'open the gateway page');
    }
    if (moduleName === 'pharmacy' && isShop) {
      ReactGA.ga('send', 'event', 'Product', 'Go to payment gateway', 'product go to payment gateway general');
    }

    if (!orderItemResponse?.extra?.virtual_pay) {
      yield navigation.push(orderItemResponse?.extra?.redirect);
    } else {
      yield put({
        type: AlertActionType.SHOW_ALERT,
        text: 'پرداخت با موفقیت انجام شد',
        severity: 'success',
      });
      yield put({
        type: FinancialActionTypes.GET_ORDER_ITEM,
        data: {
          orderItemId: orderItemId,
        },
      });
    }
  } catch (error: any) {
    if (error?.code === 422) {
      yield put({
        type: AlertActionType.SHOW_ALERT,
        text: error?.message || 'درگاه با مشکل مواجه شده است، لطفا با پشتیبانی تماس بگیرید.',
        severity: 'danger',
      });
    } else {
      yield put({
        type: AlertActionType.SHOW_ALERT,
        text: error?.message || 'درگاه با مشکل مواجه شده است، لطفا با پشتیبانی تماس بگیرید.',
        severity: 'danger',
      });
    }
    yield put({
      type: FinancialActionTypes.SET_ORDER_LOADING,
      data: {
        paymentLoading: false,
      },
    });
  }
}

function* sendCardToCardReceiptOrderWatcher() {
  try {
    const { receiptFile, orderItemId } = yield select(financeStore);

    const uploadFileResponse: { data: any } = yield uploadCardToCardReceiptFileApi({ file: receiptFile, cache: 'no-cache' });

    if (uploadFileResponse) {
      yield storeCardToCardReceiptApi({
        orderItemId: orderItemId,
        uploadReceipt: {
          '0': uploadFileResponse,
        },
      });
      yield put({
        type: AlertActionType.SHOW_ALERT,
        text: 'آپلود رسید کارت به کارت شما با موفقیت انجام شد، لطفا تا تایید آن، منتظر بمانید.',
        severity: 'success',
      });
      yield put({ type: FinancialActionTypes.SET_IS_UPLOADED_RECEIPT });
    } else {
      yield put({
        type: AlertActionType.SHOW_ALERT,
        text: 'متاسفانه در حال حاضر امکان ارسال رسید کارت به کارت شما در دسترس نیست، برای تکمیل فرایند سفارش خود با پشتیبانی تماس بگیرید',
        severity: 'danger',
      });
    }

    yield put({
      type: FinancialActionTypes.SET_ORDER_LOADING,
      data: {
        paymentLoading: false,
      },
    });
  } catch (error: any) {
    yield put({
      type: AlertActionType.SHOW_ALERT,
      text: error?.message || 'متاسفانه در حال حاضر امکان ثبت سفارش دارو با نسخه در دسترس نیست، لطفا بعدا تلاش نمایید.',
      severity: 'danger',
    });
    yield put({
      type: FinancialActionTypes.SET_ORDER_LOADING,
      data: {
        paymentLoading: false,
      },
    });
  }
}

function* sendCancelOrderWatcher() {
  try {
    const { navigation, isMobileRoute } = yield select(homeStore);
    const { reasonId, orderItemId } = yield select(financeStore);

    yield cancelOrder({ reasonId: reasonId, orderItemId: orderItemId, cache: 'no-cache' });

    yield put({
      type: AlertActionType.SHOW_ALERT,
      text: 'سفارش لغو شد. جهت بهبود رضایت مندی کاربران دلیل لغو شما حتما بررسی خواهد شد.',
      severity: 'success',
    });
    yield put({
      type: FinancialActionTypes.SET_CANCEL_ORDER_LOADING,
      data: {
        cancelOrderLoading: false,
      },
    });
    if (isMobileRoute) {
      yield navigation.push(`/mobile${routes['route.client.orders']}`);
    } else {
      yield navigation.push(routes['route.client.orders']);
    }
  } catch (error: any) {
    yield put({
      type: AlertActionType.SHOW_ALERT,
      text: error?.message || 'متاسفانه در حال حاضر امکان لغو سفارش دارو در دسترس نیست، لطفا بعدا تلاش نمایید.',
      severity: 'danger',
    });
    yield put({
      type: FinancialActionTypes.SET_CANCEL_ORDER_LOADING,
      data: {
        cancelOrderLoading: false,
      },
    });
  }
}

function* getValidDiscountCodesWatcher() {
  const { moduleId, totalPrice } = yield select(financeStore);

  try {
    const validDiscountCodesResponse: { data: Array<any> } = yield getValidDiscountCodes({ cache: 'no-cache', moduleId: moduleId, price: totalPrice });

    yield put({
      type: FinancialActionTypes.SET_VALID_DISCOUNT_CODES,
      data: {
        validDiscountCodes: validDiscountCodesResponse?.data,
      },
    });
    if (validDiscountCodesResponse?.data?.length === 1) {
      yield put({
        type: FinancialActionTypes.SET_DISCOUNT_CODE,
        data: {
          discountCode: validDiscountCodesResponse?.data[0]?.code,
        },
      });
      yield put({ type: FinancialActionTypes.GET_CODE_VALIDATION });
    }
  } catch (error: any) {
    yield put({
      type: AlertActionType.SHOW_ALERT,
      text: error?.message || 'در دریافت لیست کد های تخفیف مشکلی پیش آمده است. لطفا مجددا صفحه را بارگذاری کنید.',
      severity: 'danger',
    });

    yield put({
      type: FinancialActionTypes.SET_DISCOUNT_VALIDATION,
      data: {
        discountAmount: 0,
        discountValid: false,
        discountCode: null,
      },
    });
  }
}

function* financialsMiddleware() {
  yield all([
    takeLatest(FinancialActionTypes.GET_ORDER_ITEM, getOrderItemWatcher),
    takeLatest(FinancialActionTypes.GET_CODE_VALIDATION, getCodeValidationWatcher),
    takeLatest(FinancialActionTypes.SET_ORDER_PAYMENT, sendOrderItemWatcher),
    takeLatest(FinancialActionTypes.SEND_CARD_TO_CARD_RECEIPT_ORDER, sendCardToCardReceiptOrderWatcher),
    takeLatest(FinancialActionTypes.HANDLE_CANCEL_ORDER, sendCancelOrderWatcher),
    takeLatest(FinancialActionTypes.GET_VALID_DISCOUNT_CODES, getValidDiscountCodesWatcher),
  ]);
}

export default financialsMiddleware;
